'use client';
import { MessageCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { resolveIcon } from '../../lib/categoryIcons';
import { useTranslation } from '../../lib/i18n';
import type { BrainActivityView } from '../../lib/types';
import type { BrainStreamControl } from '../../../src/shared/wireContract';

/** Single conversation glyph with one status dot: green when free, pulsing red while working,
 * orange for user input, steady red on error. Unknown status has no dot. Unread/background
 * details stay in accessible labels. Empty stored icon = chat bubble; all surfaces use this renderer. */
export function ConversationIcon({ icon = '', size = 16, className = '', programStatus, backgroundWorking = false,
  activity, unread = activity?.unread === true, identity }: {
  icon?: string; size?: number; className?: string;
  /** Optional project identity; native status and the default saved glyph keep one owner here. */
  identity?: ReactNode;
  programStatus?: BrainStreamControl['programStatus']; backgroundWorking?: boolean;
  activity?: Omit<BrainActivityView, 'unread'> & { unread?: boolean }; unread?: boolean;
}) {
  const { t } = useTranslation();
  const Icon = resolveIcon(icon, MessageCircle);
  const state = programStatus?.state;
  const labels = {
    idle: t.chat.activityIdle, working: t.chat.activityWorking,
    blocked: programStatus?.kind === 'permission' ? t.chat.activityPermission : t.chat.activityQuestion,
    done: activity?.automation === 'scheduled' ? t.chat.activityDoneScheduled : t.chat.activityDone,
    error: t.chat.activityFailed, clear: '',
  };
  const label = [state ? labels[state] : '', ...(backgroundWorking ? [t.chat.activityBackground] : []),
    ...(unread ? [t.chat.activityUnread] : []), ...(state === 'error' && activity?.detail ? [activity.detail] : [])]
    .filter(Boolean).join(', ');
  const working = state !== undefined && state !== 'blocked' && state !== 'error'
    && (state === 'working' || backgroundWorking);
  const tone = state === undefined ? null : state === 'blocked' ? 'input'
    : state === 'error' ? 'error' : working ? 'working' : 'free';
  return <span data-conversation-icon={icon || 'MessageCircle'} data-program-status={state}
    data-activity-state={activity?.state ?? 'idle'} data-background-working={backgroundWorking || undefined}
    title={label || undefined} className={`relative inline-flex shrink-0 items-center justify-center ${className}`}
    style={{ width: `calc(${size}px + var(--spacing))`, height: `calc(${size}px + var(--spacing))` }}>
    {identity ?? <Icon size={size} strokeWidth={1.75} aria-hidden />}
    {label ? <span className="sr-only">{label}</span> : null}
    {tone ? <ConversationStateDot tone={tone} className="absolute right-0 top-0 ring-2 ring-background" /> : null}
  </span>;
}

export type ConversationStateTone = 'free' | 'working' | 'input' | 'error';

const STATE_DOT_COLOR: Record<ConversationStateTone, string> = {
  free: 'bg-success', working: 'bg-destructive', input: 'bg-warning', error: 'bg-destructive',
};

/** The conversation status dot, shared by the conversation glyph and the status filter. Decorative: the
 *  surrounding label carries the meaning. */
export function ConversationStateDot({ tone, className = '' }: { tone: ConversationStateTone; className?: string }) {
  return <span aria-hidden data-conversation-state-dot
    className={`size-1.5 shrink-0 rounded-full ${STATE_DOT_COLOR[tone]} ${tone === 'working' ? 'animate-pulse motion-reduce:animate-none' : ''} ${className}`} />;
}
